import { KEY } from '@constants/key';
import { ROUTE_PATHS } from '@constants/routes';
import { apiBaseUrl } from '@store/api/apiSlice';

/**
 * Signing in through the identity provider, as this browser sees it. The server does the
 * talking to the provider (`backend/doc/sso.md`); the browser opens the server's start
 * address in a window of its own, the server sends that window back to `/sso/callback` with a
 * one-time code, and the page that opened it redeems the code. The pair below is what makes
 * the code worthless to anybody else: only the holder of the verifier can redeem it.
 */
export interface SsoRequest {
  /** Sent along and expected back, so an answer is matched to the sign-in that asked for it. */
  state: string;
  verifier: string;
  challenge: string;
}

/** What the server appended to the callback address. */
export interface SsoAnswer {
  state?: string;
  code?: string;
  error?: string;
}

export type SsoOutcome =
  | { kind: 'answer'; request: SsoRequest; answer: SsoAnswer }
  /** No window could be opened, so this tab went to the provider itself and will come back. */
  | { kind: 'redirected' }
  /** Another sign-in was started from this page, or this one ran out of time. */
  | { kind: 'abandoned' };

/** The window could not be opened and there is nothing to fall back on. */
export class SsoWindowBlockedError extends Error { }

/**
 * The page has no WebCrypto, which the browser gives only to a secure page: this one was opened
 * over plain HTTP at an address other than localhost. A sign-in cannot even start.
 */
export class SsoInsecureContextError extends Error { }

const channelName = 'insight-sso';
const windowName = 'insight-sso';

/** As long as the server keeps a sign-in open (`ssoTransactionLifetimeSeconds`). */
const lifetimeMs = 10 * 60 * 1000;

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const createRequest = async (): Promise<SsoRequest> => {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

  return {
    state: base64Url(crypto.getRandomValues(new Uint8Array(16))),
    verifier,
    challenge: base64Url(new Uint8Array(digest)),
  };
};

/** Where the server answers this browser. It has to be this server's own public address. */
export const ssoRedirectUri = (): string => `${window.location.origin}${ROUTE_PATHS.SSO_CALLBACK}`;

export const ssoStartUrl = (request: SsoRequest): string => `${apiBaseUrl}/v1/sso/start?${new URLSearchParams({
  redirect_uri: ssoRedirectUri(),
  code_challenge: request.challenge,
  code_challenge_method: 'S256',
  state: request.state,
})}`;

const windowFeatures = (): string => {
  const width = 520;
  const height = 720;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  return `popup,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)}`;
};

let current: { abandon: () => void } | undefined;

interface Options {
  /**
   * `login` falls back to sending this tab to the provider when no window can be opened; a
   * trial run from the settings has no such fallback, since it must come back to the form.
   */
  fallback: 'redirect' | 'none';
  /**
   * The window is gone, as far as this page can tell. It cannot tell for certain: a provider
   * page may cut the tie between the two windows, and then the window reads as closed while
   * the sign-in in it goes on. So this only ends the waiting on screen, never the listening.
   */
  onWindowClosed?: () => void;
}

/**
 * Opens the provider's sign-in in a window of its own and resolves with what comes back.
 *
 * Call it straight from a click. The window is opened before anything is awaited, because a
 * browser only lets a click open one; where it goes is set once the address is known.
 */
export const openSsoWindow = async (
  urlFor: (request: SsoRequest) => string | Promise<string>, { fallback, onWindowClosed }: Options
): Promise<SsoOutcome> => {
  // Before the window: nothing flashes open and shut for a sign-in that cannot start.
  if (typeof crypto === 'undefined' || !crypto.subtle) throw new SsoInsecureContextError();

  current?.abandon();

  const opened = window.open('', windowName, windowFeatures());

  let request: SsoRequest;
  let url: string;

  try {
    request = await createRequest();
    url = await urlFor(request);
  } catch (error) {
    opened?.close();
    throw error;
  }

  if (!opened) {
    if (fallback === 'none') throw new SsoWindowBlockedError();

    // Kept for the callback page, which this tab will be when it comes back.
    sessionStorage.setItem(KEY.SSO_REQUEST, JSON.stringify({ state: request.state, verifier: request.verifier }));
    window.location.assign(url);

    return { kind: 'redirected' };
  }

  opened.location.href = url;

  return new Promise<SsoOutcome>((resolve) => {
    // A channel and not `window.opener`: it reaches this page whatever the provider's pages
    // did to the tie between the windows, and it only carries within this origin.
    const channel = new BroadcastChannel(channelName);

    const finish = (outcome: SsoOutcome) => {
      window.clearInterval(watch);
      window.clearTimeout(expiry);
      // After the acknowledgement posted just before this has left.
      window.setTimeout(() => channel.close(), 250);
      if (current === handle) current = undefined;
      resolve(outcome);
    };

    const handle = { abandon: () => finish({ kind: 'abandoned' }) };
    current = handle;

    channel.onmessage = (event: MessageEvent<SsoAnswer & { type?: string }>) => {
      const { type, ...answer } = event.data ?? {};

      // Every tab of this origin hears the channel; the state says whose answer it is.
      if (type !== 'answer' || answer.state !== request.state) return;

      channel.postMessage({ type: 'taken', state: request.state });
      finish({ kind: 'answer', request, answer });
    };

    const watch = window.setInterval(() => {
      if (!opened.closed) return;

      window.clearInterval(watch);
      onWindowClosed?.();
    }, 500);

    const expiry = window.setTimeout(() => finish({ kind: 'abandoned' }), lifetimeMs);
  });
};

/**
 * Gives up the sign-in this page is waiting for, if any: its answer, when it comes, is taken
 * by nobody. For a page that is going away, or that signed in another way meanwhile.
 */
export const abandonSsoWindow = (): void => {
  current?.abandon();
};

/**
 * From the callback page, in a window a sign-in opened: hands the answer to the page that is
 * waiting for it. Resolves `false` when nobody takes it, which is a page that has gone away
 * or has started another sign-in since.
 */
export const deliverSsoAnswer = (answer: SsoAnswer): Promise<boolean> => new Promise((resolve) => {
  const channel = new BroadcastChannel(channelName);

  const timer = window.setTimeout(() => {
    channel.close();
    resolve(false);
  }, 2000);

  channel.onmessage = (event: MessageEvent<{ type?: string; state?: string }>) => {
    if (event.data?.type !== 'taken' || event.data.state !== answer.state) return;

    window.clearTimeout(timer);
    channel.close();
    resolve(true);
  };

  channel.postMessage({ type: 'answer', ...answer });
});

/**
 * The request this tab kept before it went to the provider itself, taken out as it is read:
 * it is good for one answer.
 */
export const takeStoredSsoRequest = (): Pick<SsoRequest, 'state' | 'verifier'> | undefined => {
  const stored = sessionStorage.getItem(KEY.SSO_REQUEST);

  sessionStorage.removeItem(KEY.SSO_REQUEST);

  if (!stored) return undefined;

  try {
    const { state, verifier } = JSON.parse(stored) as Partial<SsoRequest>;

    return typeof state === 'string' && typeof verifier === 'string' ? { state, verifier } : undefined;
  } catch (_) {
    return undefined;
  }
};

/** Why `POST /v1/sso/token` refused a code, as one of the codes `ssoErrorKey` reads. */
const redemptionError = (error: unknown): string => {
  const failure = error as { status?: number; data?: { errors?: { reason?: unknown } } } | null;
  const reason = failure?.data?.errors?.reason;

  // A reason the server gives: the account was suspended after the provider's answer.
  if (typeof reason === 'string') return reason;

  // The code was spent or its minute ran out: the sign-in is to be started again.
  if (failure?.status === 400) return 'expired';

  return 'server_error';
};

/**
 * Turns the answer to a sign-in into a session, through `redeem` (code and verifier against
 * `POST /v1/sso/token`), or into the code of why not. Resolves `undefined` once signed in. The
 * one rule for the sign-in page and for the callback page, which redeems when the tab itself
 * went to the provider.
 */
export const redeemSsoAnswer = async (
  answer: SsoAnswer, verifier: string, redeem: (input: { code: string; codeVerifier: string }) => Promise<unknown>
): Promise<string | undefined> => {
  if (!answer.code) return answer.error ?? 'server_error';

  try {
    await redeem({ code: answer.code, codeVerifier: verifier });

    return undefined;
  } catch (error) {
    return redemptionError(error);
  }
};

/**
 * Why a sign-in was refused, as text for the person signing in. The server's codes are finer
 * than what a person can act on, so several share a sentence: who has to do something about
 * it is what tells them apart.
 */
export const ssoErrorKey = (code: string | undefined): string => {
  switch (code) {
    case 'access_denied': return 'SignIn_Sso_Error_AccessDenied';
    case 'account_not_provisioned': return 'SignIn_Sso_Error_NoAccount';
    case 'account_conflict': return 'SignIn_Sso_Error_Conflict';
    case 'account_suspended': return 'SignIn_AccountSuspended';
    case 'no_role_mapping':
    case 'groups_overage': return 'SignIn_Sso_Error_NotAssigned';
    case 'expired': return 'SignIn_Sso_Error_Expired';
    case 'insecure_context': return 'SignIn_Sso_Error_InsecureContext';
    default: return 'SignIn_Sso_Error_Unavailable';
  }
};
